import { Fragment, useState } from 'react';

import {
  Skeleton,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import {
  ChartPieIcon,
  ExternalLinkIcon,
  GlobeIcon,
  TrendingUpIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { brandIconUrl } from '@/shared/utils/brandDomains';

import { toDescriptionSegments } from './sourceDescription';
import { isWebSource, type Source, type SourceKind } from './sourcePanel.types';
import { useUnfurledSource } from './useUnfurledSource';

const MARK_SIZE = 32;
const FAVICON_SIZE = 20;

const KIND_ICON: Record<SourceKind, LucideIcon> = {
  statistic: ChartPieIcon,
  forecast: TrendingUpIcon,
  survey: UsersIcon,
  web: GlobeIcon,
};

const STATISTA_DOMAIN = 'statista.com';

/**
 * Hidden from assistive technology: the publication name sits beside it and already says
 * whose data this is, so announcing the mark would only repeat that.
 *
 * The icon comes from our own proxy rather than a favicon service directly, so a cited domain
 * is never disclosed to a third party by the reader's browser. That proxy 404s when it has no
 * icon, which is the common case for a small publisher, so the kind's own glyph stands in.
 */
const SourceMark = ({ source }: { source: Source }) => {
  const domain = isWebSource(source) ? source.domain : STATISTA_DOMAIN;

  // Keyed by domain so `failed` starts fresh for each one: opening a source whose icon is
  // missing must not leave the next source showing a glyph it has a real icon for.
  return <SourceMarkIcon key={domain} domain={domain} kind={source.kind} />;
};

const SourceMarkIcon = ({ domain, kind }: { domain: string; kind: SourceKind }) => {
  const [failed, setFailed] = useState(false);
  const KindIcon = KIND_ICON[kind];

  return (
    <span
      aria-hidden="true"
      data-testid="source-mark"
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-surface"
      style={{ width: MARK_SIZE, height: MARK_SIZE }}
    >
      {failed ? (
        <KindIcon
          size={FAVICON_SIZE - 4}
          data-testid="source-mark-glyph"
          className="text-foreground-subtle"
        />
      ) : (
        <img
          src={brandIconUrl(domain)}
          alt=""
          data-testid="source-mark-icon"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
          style={{ width: FAVICON_SIZE, height: FAVICON_SIZE }}
        />
      )}
    </span>
  );
};

/**
 * The body of the source drawer, shared by every record kind. Which rows appear is decided by
 * whoever built the `Source`, not here: a row with no value never reaches this component, so
 * the panel never renders an empty or placeholder line.
 *
 * Deliberately renders no chart of any kind. A figure's shape is the report's job; this panel
 * answers only where the number came from.
 */
export const SourcePanelContent = ({ source: citedSource }: { source: Source }) => {
  const { t } = useTranslation('analysis');
  const { source, isLoading } = useUnfurledSource(citedSource);

  const KindIcon = KIND_ICON[source.kind];
  // For a web record the second line is the domain itself, which says more than the words
  // "web research" would. Keys are spelled out rather than interpolated so the i18n types
  // still check them.
  const kindLabel = isWebSource(source)
    ? source.domain
    : {
        statistic: t('analysis.sourcePanel.kind.statistic'),
        forecast: t('analysis.sourcePanel.kind.forecast'),
        survey: t('analysis.sourcePanel.kind.survey'),
      }[source.kind];
  const siteLabel = isWebSource(source) ? source.site : t('analysis.sourcePanel.statista');
  const segments = isWebSource(source) ? [] : toDescriptionSegments(source.descriptionHtml ?? '');

  return (
    <div className="flex flex-col gap-xl text-foreground">
      <div className="flex shrink-0 items-center gap-md">
        <SourceMark source={source} />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {isLoading ? (
            <Skeleton className="h-5 w-40" data-testid="source-site-skeleton" />
          ) : (
            <span className="text-body-lg font-medium text-foreground">{siteLabel}</span>
          )}
          <span className="text-body-sm flex items-center gap-1.5 text-muted-foreground">
            <KindIcon size={13} aria-hidden="true" />
            {kindLabel}
          </span>
        </div>
        {source.url && (
          /* Its own provider: this panel is rendered on its own in tests and stories, where
             Radix throws without one rather than falling back to the app root's. */
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('analysis.sourcePanel.openSource')}
                  className="flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground no-underline hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <ExternalLinkIcon size={16} aria-hidden="true" />
                </a>
              </TooltipTrigger>
              <TooltipContent side="bottom">{t('analysis.sourcePanel.openSource')}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <div className="flex shrink-0 flex-col gap-1.5">
        <span className="text-body-lg font-medium text-pretty text-foreground">{source.title}</span>
        {source.subject && (
          <span className="text-body-sm text-pretty text-muted-foreground">{source.subject}</span>
        )}
      </div>

      {!isWebSource(source) && source.rows.length > 0 && (
        <dl className="flex shrink-0 flex-col overflow-hidden rounded-md bg-surface">
          {source.rows.map((row, index) => (
            <Fragment key={row.label}>
              {index > 0 && <div className="mx-2 h-px shrink-0 bg-border" />}
              <div className="flex shrink-0 gap-md px-3 py-2.5">
                <dt className="text-body-xs w-24 shrink-0 text-muted-foreground">{row.label}</dt>
                <dd className="text-body-sm min-w-0 flex-1 text-pretty text-foreground">
                  {row.value}
                </dd>
              </div>
            </Fragment>
          ))}
        </dl>
      )}

      {segments.length > 0 && (
        <div className="flex shrink-0 flex-col gap-sm">
          <span className="text-body-xs font-bold uppercase tracking-wider text-muted-foreground">
            {t('analysis.sourcePanel.description')}
          </span>
          <p className="text-body-md text-pretty text-foreground">
            {segments.map((segment, index) =>
              segment.href ? (
                <a
                  key={index}
                  href={segment.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline underline-offset-2"
                >
                  {segment.text}
                </a>
              ) : (
                <Fragment key={index}>{segment.text}</Fragment>
              ),
            )}
          </p>
        </div>
      )}
    </div>
  );
};
